/**
 * Compute a human-readable diff between two course bundles (published vs WIP).
 * Used to show a review dialog before publishing.
 */

const DEFAULT_FIELDS = [
  ['defaultStartType', 'Default start type'],
  ['defaultStartOffsetNm', 'Default start offset'],
  ['defaultStartOffsetAngle', 'Default start offset angle'],
  ['defaultStartMarkA', 'Default start mark A'],
  ['defaultStartMarkB', 'Default start mark B'],
  ['defaultFinishType', 'Default finish type'],
  ['defaultFinishOffsetNm', 'Default finish offset'],
  ['defaultFinishOffsetAngle', 'Default finish offset angle'],
  ['defaultFinishMarkA', 'Default finish mark A'],
  ['defaultFinishMarkB', 'Default finish mark B'],
];

const CARD_SCALAR_FIELDS = [
  ['name', 'Name'],
  ['vhf', 'VHF'],
  ['footnotes', 'Footnotes'],
  ['startType', 'Start type'],
  ['startOffsetNm', 'Start offset'],
  ['startOffsetAngle', 'Start offset angle'],
  ['startMarkA', 'Start mark A'],
  ['startMarkB', 'Start mark B'],
  ['finishType', 'Finish type'],
  ['finishOffsetNm', 'Finish offset'],
  ['finishOffsetAngle', 'Finish offset angle'],
  ['finishMarkA', 'Finish mark A'],
  ['finishMarkB', 'Finish mark B'],
];

// Per-course start/finish line overrides. A course can override any of the
// card-level line config, so these must be compared too or a course-level
// start/finish edit (e.g. a 1.2nm start offset on one course) reports
// "no changes detected" and can't be published.
const COURSE_LINE_FIELDS = [
  ['startType', 'start type'],
  ['startOffsetNm', 'start offset'],
  ['startOffsetAngle', 'start offset angle'],
  ['startMarkA', 'start mark A'],
  ['startMarkB', 'start mark B'],
  ['finishType', 'finish type'],
  ['finishOffsetNm', 'finish offset'],
  ['finishOffsetAngle', 'finish offset angle'],
  ['finishMarkA', 'finish mark A'],
  ['finishMarkB', 'finish mark B'],
];

// Mark fields that, if changed on an existing mark, count as a published
// modification (drives marks.modified in the diff — see MarksManager's editable
// fields: position, name/type, and the buoy characteristics appearance/lights/vhf).
const MARK_COMPARE_FIELDS = ['name', 'latitude', 'longitude', 'type', 'appearance', 'lights', 'vhf'];

function fmt(v) {
  if (v === null || v === undefined || v === '') return '(none)';
  return String(v);
}

function diffScalar(a, b, label) {
  if (fmt(a) !== fmt(b)) return `${label}: ${fmt(a)} \u2192 ${fmt(b)}`;
  return null;
}

/** Build a map keyed by id (preferred) or name (fallback). */
function indexCards(cards) {
  const byId = new Map();
  for (const card of cards) {
    const key = card.id || card.name;
    byId.set(key, card);
  }
  return byId;
}

function indexMarks(marks) {
  const byId = new Map();
  for (const mark of marks) {
    byId.set(mark.id, mark);
  }
  return byId;
}

function diffCourses(pubCourses, wipCourses) {
  const changes = [];
  const pubCount = pubCourses.length;
  const wipCount = wipCourses.length;

  if (pubCount !== wipCount) {
    const delta = wipCount - pubCount;
    const verb = delta > 0 ? `added ${delta}` : `removed ${Math.abs(delta)}`;
    changes.push(`Courses: ${pubCount} \u2192 ${wipCount} (${verb})`);
  }

  // Identity key. Grid courses are identified by their compass cell
  // (directionCode + courseNumber). Named courses (directionCode == null) ALL
  // share the null/0 cell, so a card with many named courses (e.g. Cork's
  // global 1-31 numbering) would collapse to a single map entry and compare
  // every named course against the last one — false positives and missed
  // tag-only changes. Key named courses by their stable id instead. (ra-gbrd.19)
  const courseKey = (c) =>
    c.directionCode == null ? `named:${c.id}` : `${c.directionCode}${c.courseNumber}`;
  // Human-readable label for change messages (id is opaque to a race officer).
  const courseLabel = (c) =>
    c.directionCode == null ? (c.name || 'Named course') : `${c.directionCode}${c.courseNumber}`;

  const pubMap = new Map();
  for (const c of pubCourses) {
    pubMap.set(courseKey(c), c);
  }
  const matchedPubKeys = new Set();

  for (const wc of wipCourses) {
    const pc = pubMap.get(courseKey(wc));
    if (!pc) {
      // No published course with this identity. A pure count change is already
      // summarised above, but a SAME-COUNT replacement (delete one named course,
      // add another) wouldn't be — without this the publish dialog would say
      // "No changes detected" for a real swap. (ra-gbrd.19, codex review.)
      changes.push(`${courseLabel(wc)} added`);
      continue;
    }
    matchedPubKeys.add(courseKey(wc));
    const label = courseLabel(wc);

    // Named courses are keyed by id, so a rename keeps the same key. The name is
    // serialized into the bundle and shown in the app, so a name-only edit is a
    // real, app-visible change — diff it or the publish dialog says "No changes
    // detected". (Grid courses have no user-facing name.) (ra-gbrd.19)
    if (wc.directionCode == null) {
      const nd = diffScalar(pc.name, wc.name, 'Named course name');
      if (nd) changes.push(nd);
    }

    // Check bearing
    const bd = diffScalar(pc.bearing, wc.bearing, `${label} bearing`);
    if (bd) changes.push(bd);

    // Named-course tags (ra-gbrd.19). Without these, changing only a named
    // course's wind sector or start area after a publish would diff to nothing
    // and the publish dialog would falsely report "No changes detected".
    const wsd = diffScalar(pc.windDirection, wc.windDirection, `${label} wind sector`);
    if (wsd) changes.push(wsd);
    const sad = diffScalar(pc.startArea, wc.startArea, `${label} start area`);
    if (sad) changes.push(sad);

    // Check mark sequence
    const pubMarks = (pc.markIds || []).join(',');
    const wipMarks = (wc.markIds || []).join(',');
    if (pubMarks !== wipMarks) {
      changes.push(`${label} marks changed`);
    }

    // Check rounding directions
    const pubDirs = (pc.markDirections || []).join(',');
    const wipDirs = (wc.markDirections || []).join(',');
    if (pubDirs !== wipDirs) {
      changes.push(`${label} rounding changed`);
    }

    // Check per-course start/finish line overrides
    for (const [field, label2] of COURSE_LINE_FIELDS) {
      const ld = diffScalar(pc[field], wc[field], `${label} ${label2}`);
      if (ld) changes.push(ld);
    }
  }

  // Published courses with no WIP counterpart were removed. (Same-count
  // replacement otherwise slips through the count summary above.)
  for (const pc of pubCourses) {
    if (!matchedPubKeys.has(courseKey(pc))) {
      changes.push(`${courseLabel(pc)} removed`);
    }
  }

  return changes;
}

function diffCard(pub, wip) {
  const changes = [];

  for (const [field, label] of CARD_SCALAR_FIELDS) {
    const d = diffScalar(pub[field], wip[field], label);
    if (d) changes.push(d);
  }

  // Sailing classes — show which were added/removed/changed
  const pubClasses = pub.sailing_classes || [];
  const wipClasses = wip.sailing_classes || [];
  const pubClassNames = new Set(pubClasses.map((c) => c.name || c.id || ''));
  const wipClassNames = new Set(wipClasses.map((c) => c.name || c.id || ''));
  const addedClasses = [...wipClassNames].filter((n) => n && !pubClassNames.has(n));
  const removedClasses = [...pubClassNames].filter((n) => n && !wipClassNames.has(n));
  if (addedClasses.length > 0) changes.push(`Classes added: ${addedClasses.join(', ')}`);
  if (removedClasses.length > 0) changes.push(`Classes removed: ${removedClasses.join(', ')}`);
  if (addedClasses.length === 0 && removedClasses.length === 0 && pubClasses.length !== wipClasses.length) {
    changes.push(`Sailing classes: ${pubClasses.length} \u2192 ${wipClasses.length}`);
  }

  // Card lozenges (generic key-value properties). Compare the rendered
  // key=value pairs so a lozenge-only add/edit/remove is published rather than
  // reported as "no changes detected" (ra-hi9d).
  const fmtProps = (list) =>
    (list || [])
      .map((p) => `${(p.key || '').trim()}=${(p.value || '').trim()}`)
      .filter((s) => s !== '=');
  const pubProps = fmtProps(pub.properties);
  const wipProps = fmtProps(wip.properties);
  if (pubProps.join('|') !== wipProps.join('|')) {
    changes.push(
      `Lozenges: ${pubProps.join(', ') || '(none)'} → ${wipProps.join(', ') || '(none)'}`,
    );
  }

  // Course-level diff
  const courseChanges = diffCourses(pub.courses || [], wip.courses || []);
  changes.push(...courseChanges);

  return changes;
}

/**
 * Compute a human-readable diff between a published bundle and a WIP bundle.
 *
 * @param {object|null} published - The published bundle (null for first publish)
 * @param {object} wip - The WIP bundle
 * @returns {{ cards: { added: string[], removed: string[], modified: Array<{name: string, changes: string[]}> }, marks: { added: string[], removed: string[], modified: string[] }, defaults: string[], isEmpty: boolean }}
 */
export function computeBundleDiff(published, wip) {
  const pub = published || { course_cards: [], marks: [] };

  const cards = { added: [], removed: [], modified: [] };
  const marks = { added: [], removed: [], modified: [] };
  const defaults = [];

  // --- Bundle defaults ---
  for (const [field, label] of DEFAULT_FIELDS) {
    const d = diffScalar(pub[field], wip[field], label);
    if (d) defaults.push(d);
  }

  // --- Cards ---
  const pubCards = indexCards(pub.course_cards || []);
  const wipCards = indexCards(wip.course_cards || []);

  for (const [key, wipCard] of wipCards) {
    const pubCard = pubCards.get(key);
    if (!pubCard) {
      cards.added.push(wipCard.name || key);
    } else {
      const changes = diffCard(pubCard, wipCard);
      if (changes.length > 0) {
        cards.modified.push({ name: wipCard.name || key, changes });
      }
    }
  }

  for (const [key, pubCard] of pubCards) {
    if (!wipCards.has(key)) {
      cards.removed.push(pubCard.name || key);
    }
  }

  // --- Marks ---
  const pubMarks = indexMarks(pub.marks || []);
  const wipMarks = indexMarks(wip.marks || []);

  for (const [id, wipMark] of wipMarks) {
    const pubMark = pubMarks.get(id);
    if (!pubMark) {
      marks.added.push(wipMark.name || id);
    } else {
      // Compare every field a committee edit can change (position, identity,
      // and the buoy characteristics that survive publish — ra-gbrd.29), not
      // just position: a lights/VHF/appearance edit is a real published change
      // and must show in the review dialog too.
      const changed = MARK_COMPARE_FIELDS.some((f) => pubMark[f] !== wipMark[f]);
      if (changed) {
        marks.modified.push(wipMark.name || id);
      }
    }
  }

  for (const [id, pubMark] of pubMarks) {
    if (!wipMarks.has(id)) {
      marks.removed.push(pubMark.name || id);
    }
  }

  const isEmpty =
    cards.added.length === 0 &&
    cards.removed.length === 0 &&
    cards.modified.length === 0 &&
    marks.added.length === 0 &&
    marks.removed.length === 0 &&
    marks.modified.length === 0 &&
    defaults.length === 0;

  return { cards, marks, defaults, isEmpty };
}
